import * as React from "react";
import {NextButton} from "components/GettingStarted/NextButton";
import routeLinks from "routeLinks";
import PermissionCheck from "components/PermissionCheck/PermissionCheck";
import Permission from "client/resources/permission";
import Callout, {CalloutType} from "components/Callout/Callout";
import styles = require("./style.less");
import ProjectResource from "client/resources/projectResource";
import ReleaseResource from "client/resources/releaseResource";

export interface WhatsNext {
    text: string;
    element: React.ReactNode;
}

export default function getWhatsNext(isInEnvironmentPage: boolean,
                                     next: string,
                                     projects: ProjectResource[],
                                     releases: ReleaseResource[],
                                     onClick?: () => void): WhatsNext {
    switch (next) {
        case "Infrastructure.CreatedEnvironment":
            return {
                text: "To get started, create an environment.",
                element: <span className={styles.whatsNextButtonsContainer}><NextButton onClick={onClick} defaultLabel="Create environment" items={[
                    {
                        label: "Create environment",
                        link: routeLinks.infrastructure.environments.root
                    }
                ]} /></span>
            };

        case "Infrastructure.CreatedMachineOrAccount":
            return {
                text: "Your new environment needs a deployment target.",
                element: <span className={styles.whatsNextButtonsContainer}>
                        <PermissionCheck permission={Permission.MachineCreate} wildcard={true} alternate={<Callout type={CalloutType.Information}>
                            The {Permission.MachineCreate} permission is required to add a deployment target
                        </Callout>}>
                            <NextButton onClick={onClick} defaultLabel="Add deployment target" items={[
                                {
                                    label: "Add deployment target",
                                    link: isInEnvironmentPage ? routeLinks.infrastructure.machines.discover()
                                        : routeLinks.infrastructure.environments.root
                                }
                            ]} />
                        </PermissionCheck>
                        <PermissionCheck permission={Permission.AccountCreate} wildcard={true} alternate={<Callout type={CalloutType.Information}>
                            The {Permission.AccountCreate} permission is required to add an account
                        </Callout>}>
                            <NextButton onClick={onClick} defaultLabel="Add an account" items={[
                                {
                                    label: "Add an account",
                                    link: routeLinks.infrastructure.accounts.root
                                }
                            ]} />
                        </PermissionCheck>
                    </span>
            };

        case "Package":
            return {
                text: "Upload package manually, automatically or via an external feed.",
                element: <span className={styles.whatsNextButtonsContainer}>
                        <PermissionCheck permission={Permission.BuiltInFeedPush} wildcard={true} alternate={<Callout type={CalloutType.Information}>
                            The {Permission.BuiltInFeedPush} permission is required to upload a package
                        </Callout>}>
                            <NextButton onClick={onClick} defaultLabel="Upload package" items={[
                                {
                                    label: "Upload package",
                                    link: routeLinks.library.builtInRepository.root
                                }
                            ]} />
                        </PermissionCheck>
                        <PermissionCheck permission={Permission.BuiltInFeedPush} wildcard={true} alternate={<Callout type={CalloutType.Information}>
                            The {Permission.BuiltInFeedPush} permission is required to upload a package
                        </Callout>}>
                            <NextButton onClick={onClick} defaultLabel="Add external feed" items={[
                                {
                                    label: "Add external feed",
                                    link: routeLinks.library.feeds
                                }
                            ]} />
                        </PermissionCheck>
                    </span>
            };

        case "Project.CreatedProject":
            return {
                text: "You're making great progress!",
                element: <span className={styles.whatsNextButtonsContainer}>
                        <PermissionCheck permission={Permission.ProjectCreate} projectGroup="*"
                                         alternate={<Callout type={CalloutType.Information}>
                                             The {Permission.ProjectCreate} permission is required to add a project
                                         </Callout>}>
                            <NextButton onClick={onClick} defaultLabel="Add project" items={[
                                {
                                    label: "Add project",
                                    link: routeLinks.projects
                                }
                            ]} />
                        </PermissionCheck>
                    </span>
            };

        case "Project.AddedProjectStep":
            return {
                text: "To create a release, a deployment process must be configured.",
                element: <span className={styles.whatsNextButtonsContainer}><NextButton onClick={onClick}
                    defaultLabel="Define your deployment process" items={projects.map(p => (
                    {
                        label: p.Name,
                        link: routeLinks.project(p).process.root
                    }
                ))} /></span>
            };

        case "Deploy.CreatedRelease":
            return {
                text: "You can now create a release.",
                element: <span className={styles.whatsNextButtonsContainer}>
                        <PermissionCheck permission={Permission.ReleaseCreate} wildcard={true}
                                         alternate={<Callout type={CalloutType.Information}>
                                             The {Permission.ReleaseCreate} permission is required to deploy the release
                                         </Callout>}>
                            <NextButton onClick={onClick} defaultLabel="Create release" items={projects.map(p => (
                                {
                                    label: p.Name,
                                    link: routeLinks.project(p).releases
                                }
                            ))} />
                        </PermissionCheck>
                    </span>
            };

        case "Deploy.DeployedRelease":
            const deployRelease = (() => {
                const project = projects.find(p =>
                    p.Id === releases[0].ProjectId
                );

                return {
                    label: project.Name,
                    link: routeLinks.project(project).root
                };
            });

            const deployMultipleRelease = (() => {
                let projectIds: string[] = [];

                projectIds = releases.reduce((state, r) => {
                    if (!state.includes(r.ProjectId)) {
                        state.push(r.ProjectId);
                    }

                    return state;
                }, projectIds);

                return projects
                    .filter(p => projectIds.includes(p.Id))
                    .map(p => (
                        {
                            label: p.Name,
                            link: routeLinks.project(p).root
                        }
                    ));
            });

            return {
                text: "Woohoo! Your first release is ready to deploy.",
                element: <span className={styles.whatsNextButtonsContainer}>
                        <PermissionCheck permission={Permission.DeploymentCreate} wildcard={true} alternate={<Callout type={CalloutType.Information}>
                            The {Permission.DeploymentCreate} permission is required to deploy the release
                        </Callout>}>
                            {releases.length === 1 && <NextButton onClick={onClick} defaultLabel="Deploy" items={[deployRelease()]} />}
                            {releases.length > 1 && <NextButton onClick={onClick} defaultLabel="Deploy" items={deployMultipleRelease()} />}
                        </PermissionCheck>
                    </span>
            };
        default:
            return {
                text: null,
                element: null
            };
    }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!./app/components/GettingStarted/WhatsNext/WhatsNext.tsx